import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingLarge} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslate} from "@repo/i18n";
import {scrollToElement} from "@repo/util-scrolling";

import BottomSheet from "#components/bottom-sheet/bottom-sheet.tsx";

import {DownloadCSVButton} from "./download-csv-button.tsx";
import {isTopLevelStandard} from "./helpers/transform-set-of-standards-to-tree.ts";
import {BookIcon, MathIcon, ScienceIcon} from "./icons.tsx";
import {NestedStandardsList} from "./nested-standards-list.tsx";
import ResponsiveTwoColumns from "./responsive-two-columns.tsx";
import StandardsMappingTopLevelContent from "./standards-mapping-top-level-content.tsx";
import {StandardsNavHeader} from "./standards-nav-header.tsx";
import {StandardsNavModalLauncher} from "./standards-nav-modal-launcher.tsx";
import StandardsTreeCards from "./standards-tree-cards.tsx";
import type {Standard} from "./types.ts";
import useResponsiveLayout from "./use-responsive-layout.ts";

type Props = {
    scrollToStandardId?: string;
    subTreeToShow: Standard | null | undefined;
    setName: string;
    setTrees: Array<Standard>;
    setDomains: Array<string | null | undefined> | null | undefined;
    standardRegionIds: Array<string> | null | undefined;
};

function getHeading(domain: string): {
    icon: React.ReactElement;
    title: string;
} {
    const mathTitle = doNotTranslate`Math`;
    const scienceTitle = doNotTranslate`Science`;
    const defaultTitle = doNotTranslate`Standards`;

    const mathIcon = <MathIcon />;
    const scienceIcon = <ScienceIcon />;
    const defaultIcon = <BookIcon />;

    // If we're looking at science or math standard sets, we know what to do.
    // Otherwise give it a suitable default.
    // TODO: Add more domains as we support them.
    if (domain === "science") {
        return {
            icon: scienceIcon,
            title: scienceTitle,
        };
    }

    if (domain === "math") {
        return {
            icon: mathIcon,
            title: mathTitle,
        };
    }

    return {
        icon: defaultIcon,
        title: defaultTitle,
    };
}

export const StandardsMappingContent = (props: Props): React.ReactElement => {
    const layout = useResponsiveLayout();
    const {
        scrollToStandardId,
        subTreeToShow,
        setName,
        setTrees,
        setDomains,
        standardRegionIds,
    } = props;

    // Set timeout of 0 after effect is triggereed to wait for the current
    // sync call stack to empty. This is necessary because aphrodite styles
    // haven't been applied when useEffect runs so the wrong offset height is
    // scrolled to.
    useTimeout(() => {
        if (!scrollToStandardId) {
            return;
        }

        const standardHeader = document.getElementById(scrollToStandardId);
        if (!standardHeader) {
            return;
        }

        scrollToElement(standardHeader);
    }, 0);

    // TODO(starin): When/if we decide to have multiple country standars hub
    // pages, we'll want to get this data from the set of standards or anohter
    // place. For now hardcoding "united-states"
    const countrySlug = "united-states";

    const domain = setDomains?.[0] ?? "";
    const heading = getHeading(domain);

    const navList = (props?: {closeModal: () => void}) => (
        <>
            {layout === "mobile" && <Strut size={120} />}
            <NestedStandardsList
                header={
                    <StandardsNavHeader
                        countrySlug={countrySlug}
                        domainIcon={heading.icon}
                        domainTitle={heading.title}
                        regionIds={standardRegionIds}
                    />
                }
                onSelectStandard={props?.closeModal}
                standards={setTrees}
                selectedId={subTreeToShow?.standardId || ""}
            />
        </>
    );

    let nav;

    if (layout === "mobile") {
        nav = (
            <BottomSheet style={styles.bottomSheet} content={navList}>
                {({openModal}) => (
                    <StandardsNavModalLauncher
                        openModal={openModal}
                        setName={setName}
                    />
                )}
            </BottomSheet>
        );
    } else {
        nav = navList();
    }

    const showStandardsContent =
        subTreeToShow &&
        !isTopLevelStandard(subTreeToShow.standardId, setTrees);

    const content = showStandardsContent ? (
        <StandardsTreeCards
            standard={subTreeToShow}
            subTreeTitle={subTreeToShow.parentStandardDescription || ""}
            layout={layout}
        />
    ) : (
        <StandardsMappingTopLevelContent
            subTreeToShow={subTreeToShow}
            setTrees={setTrees}
            domain={domain}
        />
    );

    return (
        <ResponsiveTwoColumns
            nav={nav}
            content={
                <Body>
                    <View>
                        <HeadingLarge style={styles.marginSmall}>
                            {setName}{" "}
                            <DownloadCSVButton
                                standards={setTrees}
                                title={setName}
                            />
                        </HeadingLarge>
                        {content}
                    </View>
                </Body>
            }
        />
    );
};

const styles = {
    bottomSheet: {
        maxHeight: "100vh",
    },

    marginSmall: {
        margin: spacing.small_12,
    },

    highLevelTitle: {
        paddingBottom: spacing.medium_16,
    },

    topLevelBody: {
        lineHeight: "22px",
        paddingBottom: spacing.medium_16,
    },

    buttonContainer: {
        flexDirection: "row",
    },
} as const;
